/* eslint-disable no-param-reassign,id-length */
import { round } from '@deltasierra/utilities-math';
import { Color } from './colors';

export type ColorScheme = 'cmyk' | 'hsl' | 'hsv' | 'rgb';

export interface ColorSchemeHelper<T extends Color> {
    scheme: ColorScheme;
    isMatch(color: object): color is T;
    parse(color: string): T | undefined;
    sanitise(color: T): T;
    toString(color: T): string;
    fromRgb(color: RGB): T;
    toRgb(color: T, options?: { round?: boolean }): RGB;
    areEqual(color1: T | null | undefined, color2: T | null | undefined): boolean;
}

export interface SanitisationOptions {
    min: number;
    max: number;
    default: number;
    roundPrecision?: number;
    isPercentage: boolean;
}

export function sanitiseValue(value: number | undefined, options: SanitisationOptions): number {

    if ((value === undefined || value.toString() === '') && options.default !== undefined) {
        return options.default;
    }

    if (typeof value === 'string') {
        value = parseFloat(`${value}`);
    }

    value = options.roundPrecision === undefined ? value : round(value!, options.roundPrecision);

    value = Math.max(value!, options.min);
    value = Math.min(value, options.max);

    return value;
}

const byteSanitisationOptions: SanitisationOptions = {
    default: 0,
    isPercentage: false,
    max: 255,
    min: 0,
    roundPrecision: 0,
};

export const degreeSanitisationOptions: SanitisationOptions = {
    default: 0,
    isPercentage: false,
    max: 360,
    min: 0,
    roundPrecision: 0,
};

export const percentSanitisationOptions: SanitisationOptions = {
    default: 0,
    isPercentage: true,
    max: 1,
    min: 0,
    roundPrecision: 3,
};

export const opacitySanitisationOptions: SanitisationOptions = {
    default: 1,
    isPercentage: false,
    max: 1,
    min: 0,
    roundPrecision: 2,
};

export function sanitiseByte(value: number, shouldRound = true): number {
    const options: SanitisationOptions = {
        default: byteSanitisationOptions.default,
        isPercentage: byteSanitisationOptions.isPercentage,
        max: byteSanitisationOptions.max,
        min: byteSanitisationOptions.min,
        roundPrecision: shouldRound ? 0 : 10,
    };
    return sanitiseValue(value, options);
}

export function sanitiseDegree(value: number): number {
    return sanitiseValue(value, degreeSanitisationOptions);
}

export function sanitisePercent(value: number): number {
    return sanitiseValue(value, percentSanitisationOptions);
}

export function sanitiseOpacity(value: number | undefined): number {
    return sanitiseValue(value, opacitySanitisationOptions);
}

function parseValue(value: string, options: SanitisationOptions): number {

    if (value === undefined) {
        value = options.isPercentage ? `${options.default * 100}%` : options.default.toString();
    }

    const isPercentage = value.substr(value.length - 1, 1) === '%';

    return isPercentage
        ? (parseFloat(value.substr(0, value.length - 1)) / 100) * (options.max - options.min) + options.min
        : parseFloat(value);
}

export function parseDegree(value: string): number {
    return parseValue(value, degreeSanitisationOptions);
}

export function parsePercent(value: string): number {
    return parseValue(value, percentSanitisationOptions);
}

export function parseOpacity(value: string): number {
    return parseValue(value, opacitySanitisationOptions);
}

/**
 * Represents a color in RGB color space.
 * Red, Green and Blue values are expressed as integers between 0 and 255.
 */
export interface RGB {
    r: number;
    g: number;
    b: number;
    a?: number;
}

export type RGBOutputFormat = 'hex' | 'rgba';

export class RGBHelper implements ColorSchemeHelper<RGB> {
    public scheme: ColorScheme = 'rgb';

    public isMatch(color: object): color is RGB {
        return (
            'r' in color &&
            'g' in color &&
            'b' in color
        );
    }

    public parse(color: string): RGB | undefined {
        const result =
            this.parseRGBFunction(color) ||
            this.parseRGBAFunction(color) ||
            this.parseRGBHex3(color) ||
            this.parseRGBHex6(color);

        return result;
    }

    public sanitise(color: RGB, shouldRound = true): RGB {
        return {
            a: sanitiseOpacity(color.a),
            b: sanitiseByte(color.b, shouldRound),
            g: sanitiseByte(color.g, shouldRound),
            r: sanitiseByte(color.r, shouldRound),
        };
    }

    public toString(color: RGB, format: RGBOutputFormat = 'rgba'): string {
        if (format === 'hex') {
            let hex = `#${this.byteToHex(color.r)}${this.byteToHex(color.g)}${this.byteToHex(color.b)}`;

            if (color.a !== undefined && color.a !== 1) {
                hex += this.byteToHex(color.a);
            }

            return hex;
        }

        if (color.a === undefined || color.a === 1) {
            return `rgb(${color.r}, ${color.g}, ${color.b})`;
        }

        return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
    }

    public fromRgb(color: RGB): RGB {
        return this.toRgb(color);
    }

    public toRgb(color: RGB, options?: { round?: boolean }): RGB {
        let shouldRound = true;
        if (options && options.round !== undefined) {
            shouldRound = options.round;
        }

        const result = this.sanitise(color, shouldRound);

        return result;
    }

    public areEqual(colorA: RGB | null, colorB: RGB | null): boolean {
        const aIsNull = colorA === null;
        const bIsNull = colorB === null;

        if (aIsNull || bIsNull) {
            return aIsNull === bIsNull;
        }

        const aIsOpaque = colorA.a === 1 || colorA.a === undefined;
        const bIsOpaque = colorB.a === 1 || colorB.a === undefined;

        const areEqual =
            colorA.r === colorB.r &&
            colorA.g === colorB.g &&
            colorA.b === colorB.b &&
            (colorA.a === colorB.a || aIsOpaque === bIsOpaque);

        return areEqual;
    }

    private parseRGBFunction(color: string): RGB | undefined {
        color = `${color}`.trim();

        const pattern = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i;
        const match = pattern.exec(color);

        if (!match) {
            return undefined;
        }

        let result = {
            b: parseInt(match[3], 10),
            g: parseInt(match[2], 10),
            r: parseInt(match[1], 10),
        };

        result = this.sanitise(result);

        return result;
    }

    private parseRGBAFunction(color: string): RGB | undefined {
        color = `${color}`.trim();

        const pattern = /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+))\s*\)$/i;
        const match = pattern.exec(color);

        if (!match) {
            return undefined;
        }

        const result = this.sanitise({
            a: parseOpacity(match[4]),
            b: parseInt(match[3], 10),
            g: parseInt(match[2], 10),
            r: parseInt(match[1], 10),
        });

        return result;
    }

    private parseRGBHex3(color: string): RGB | undefined {
        color = `${color}`.trim();

        const pattern = /^#[\dabcdef]{3}$/i;
        const match = pattern.exec(color);

        if (!match) {
            return undefined;
        }

        const channels = [match[0][1], match[0][2], match[0][3]].map(v => v + v).map(v => parseInt(v, 16));

        const result = this.sanitise({ b: channels[2], g: channels[1], r: channels[0] });

        return result;
    }

    private parseRGBHex6(color: string): RGB | undefined {
        color = `${color}`.trim();

        const pattern = /^#[\dabcdef]{6}$/i;
        const match = pattern.exec(color);

        if (!match) {
            return undefined;
        }

        const result = this.sanitise({
            b: parseInt(match[0].substr(5, 2), 16),
            g: parseInt(match[0].substr(3, 2), 16),
            r: parseInt(match[0].substr(1, 2), 16),
        });

        return result;
    }

    private byteToHex(value: number): string {
        value = Math.round(value);

        if (value < 0 || value > 255) {
            throw new Error(`Cannot convert byte to hex: Out of bounds: ${value}`);
        }

        let result = value.toString(16);
        result = result.length === 1 ? `0${result}` : result;

        return result;
    }
}
